import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import StepActions from "./StepActions";
import Step3Preview from "./Step3Preview";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { createReportItem, updateReportItem } from '../../services/activityReportService';

// --- Validation Schema ---
const schema = yup.object({
  // KPI metrics
  metrics: yup.array().of(
    yup.object({
      id: yup.number(),
      //type: yup.string().required("Metric type is required"),
      label: yup.string().required(),
      description: yup.string().optional(),
    })
  ).min(1, "At least one metric is required"),

  // Documents
  documentsDescription: yup.string().optional(),

  // News
  newsDescription: yup.string().optional(),

  // Custom Fields
  customFields: yup.array().of(
    yup.object({
      id: yup.number(),
      field_name: yup.string().required("Field name is required"),
      field_description: yup.string().optional(),
    })
  ).optional(),
});

const KPI_METRICS = [
  { label: "Revenue" },
  { label: "Cash Balance" },
  { label: "Runway" },
  { label: "Net Income" },
  { label: "Total Headcount" },
  { label: "ARR" },
];

const CUSTOM_FIELD_OPTIONS = [
   "CARR", "Quarterly Revenue", "Monthly Revenue", "AUM",
  "Deployment Revenue", "Rev. Run Rate", "Quarterly EBITDA", "Monthly GMV", "YTD Bookings"
];

// --- Modal Components (unchanged) ---
const SendReportModal = ({ open, onClose, onSaveDraft, onContinue, isSubmitting }) => (
  <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "16px", p: 1, maxWidth: "400px" } }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "18px", fontFamily: "serif" }}>Send Report Request</Typography>
      <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
    </Box>
    <DialogContent>
      <Typography sx={{ fontSize: "14px", color: "#4B5563", mb: 2 }}>
        {isSubmitting ? "Submitting your report request..." : "You have not filled in all the required information:"}
      </Typography>
      {!isSubmitting && (
        <Box component="ul" sx={{ fontSize: "14px", color: "#4B5563", pl: 2, mb: 2 }}>
          <li>Companies</li>
          <li>KPI</li>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ pb: 3, px: 3, gap: 1 }}>
      {!isSubmitting && (
        <>
          <Button onClick={onSaveDraft} sx={{ textTransform: "none", color: "#1A1919", fontWeight: 600 }}>Save as Draft</Button>
          <Button onClick={onContinue} variant="contained" sx={{ textTransform: "none", bgcolor: "#1E517B", borderRadius: "8px", fontWeight: 600 }}>
            Continue Filling
          </Button>
        </>
      )}
    </DialogActions>
  </Dialog>
);

const LeaveWithoutSavingModal = ({ open, onClose, onDiscard, onSaveDraft }) => (
  <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "16px", p: 1, maxWidth: "400px" } }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "18px", fontFamily: "serif" }}>Leave without saving?</Typography>
      <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
    </Box>
    <DialogContent>
      <Typography sx={{ fontSize: "14px", color: "#4B5563" }}>
        You've started creating a new report request, but your changes haven't been saved yet.
        <br /><br />
        If you leave now, you can either save your progress as a draft, discard everything you've entered, or stay on this page and continue editing.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ pb: 3, px: 3, gap: 1 }}>
      {/* <Button onClick={onDiscard} sx={{ textTransform: "none", color: "#1A1919", fontWeight: 600 }}>Discard Changes</Button> */}
      <Button onClick={onSaveDraft} variant="contained" sx={{ textTransform: "none", bgcolor: "#1E517B", borderRadius: "8px", fontWeight: 600 }}>
        Save as Draft
      </Button>
    </DialogActions>
  </Dialog>
);

const SectionWrapper = ({ title, icon, children, action }) => (
  <Box
    sx={{
      bgcolor: "#FAFAFA",
      borderRadius: "12px",
      border: "1px solid #E5E7EB",
      p: 3,
      mb: 3,
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#1A1919", textTransform: "uppercase" }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const Step3Report = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from previous steps
  const settingsData = location.state?.settingsData;
  const companiesData = location.state?.company;

  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Modal States
  const [openSendModal, setOpenSendModal] = useState(false);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  const [isDraftSaved, setIsDraftSaved] = useState(false);

  const existingReportData = location.state?.reportData;

  const isEditMode = location.state?.isEditMode || false;

  // Redirect if no data from previous steps
  useEffect(() => {
    if (!settingsData || !companiesData) {
      navigate('/create-request');
    }
  }, [settingsData, companiesData, navigate]);

  // Initialize React Hook Form
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      //metrics: [{ id: Date.now(), type: "", description: "" }],
      metrics: KPI_METRICS.map(m => ({ label: m.label, description: "" })),
      documentsDescription: "",
      newsDescription: "",
      customFields: [],
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (existingReportData) {
      const rd = Array.isArray(existingReportData)
        ? existingReportData[0]
        : existingReportData;

      reset({
        metrics: rd?.metrics?.length
          ? rd.metrics
          : KPI_METRICS.map(m => ({ label: m.label, description: "" })),
        documentsDescription: rd?.documents_description || rd?.documentsDescription || "",
        newsDescription: rd?.news_description || rd?.newsDescription || "",
        customFields: rd?.custom_fields || rd?.customFields || [],
      });
    }
  }, [existingReportData, reset]);

  // Watch metrics for validation
  const watchMetrics = watch("metrics");

  // Handle Add Metric
  const handleAddMetric = () => {
    const currentMetrics = getValues("metrics") || [];
    const newMetric = { id: Date.now(), type: "", description: "" };
    setValue("metrics", [...currentMetrics, newMetric]);
  };

  // Handle Remove Metric
  const handleRemoveMetric = (id) => {
    const currentMetrics = getValues("metrics") || [];
    setValue("metrics", currentMetrics.filter(m => m.id !== id));
  };

  // Handle Add Custom Field
  const handleAddCustomField = () => {
    const currentFields = getValues("customFields") || [];
    const newField = { id: Date.now(), field_name: "", field_description: "" };
    setValue("customFields", [...currentFields, newField]);
  };

  // Handle Remove Custom Field
  const handleRemoveCustomField = (id) => {
    const currentFields = getValues("customFields") || [];
    setValue("customFields", currentFields.filter(f => f.id !== id));
  };

  // Form Submission
  const onSubmit = async (reportData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Combine all data from all three steps
      const completeFormData = {
        // Step 1: Settings
        report_name: settingsData?.report_name,
        ...settingsData,

        // Step 2: Companies
        company: [...companiesData],

        // Step 3: Report
        request_data:
        {
          metrics: reportData.metrics,
          documents_description: reportData.documentsDescription,
          news_description: reportData.newsDescription,
          custom_fields: reportData.customFields,
        }
        ,
        customkpis: (reportData.customFields || [])
          .filter(f => f.field_name)
          .map(f => {
            const keyMap = {
              "ARR": "top_line_kpi",
              "CARR": "carr",
              "Quarterly Revenue": "quarterly_revenue",
              "Monthly Revenue": "monthly_revenue",
              "AUM": "aum",
              "Deployment Revenue": "deployment_revenue",
              "Rev. Run Rate": "rev_run_rate",
              "Quarterly EBITDA": "quarterly_ebitda",
              "Monthly GMV": "monthly_gmv",
              "YTD Bookings": "ytd_bookings",
            };
            return keyMap[f.field_name] || f.field_name;
          }),
        report_sent_on: new Date().toISOString(),
        status: isDraftSaved ? 4 : 3, // 4 for draft, 3 for new request
        created_at: new Date().toISOString(),
      };



      console.log("Submitting complete form data:", completeFormData);
      console.log("=== SUBMITTING completeFormData ===", JSON.stringify({
        report_name: completeFormData.report_name,
        company: completeFormData.company,
        company_length: completeFormData.company?.length,
      }, null, 2));

      if (isEditMode && location.state?.requestId) {
        await updateReportItem(location.state.requestId, completeFormData);
      } else {
        await createReportItem(completeFormData);
      }

      setSubmitSuccess(true);
      setOpenSendModal(false);

      // Redirect after success
      setTimeout(() => {
        navigate('/activity-reports');
      }, 3000);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred');
      setOpenSendModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Send button click
  const handleSendClick = () => {
    const metrics = getValues("metrics");
    //const hasValidMetrics = metrics && metrics.length > 0 && metrics[0].type !== "";
    const hasValidMetrics = metrics && metrics.length > 0;

    if (!hasValidMetrics) {
      setOpenSendModal(true);
    } else {
      handleSubmit(onSubmit)();
    }
  };

  // Handle Save as Draft
  const handleSaveDraft = async () => {

  setIsDraftSaved(true);

  await handleSubmit(onSubmit)();

  setOpenLeaveModal(false);
  setOpenSendModal(false);
};

  // Handle Discard Changes
  const handleDiscard = () => {
    navigate('/requests');
  };

  // Success view
  if (submitSuccess) {
    return (
      <Box sx={{ pb: 12 }}>
        <Box sx={{ maxWidth: 850, mx: "auto", mt: 4, textAlign: "center" }}>
          {/* <Alert severity="success" sx={{ mb: 3, borderRadius: "12px" }}>
            Report request submitted successfully!
          </Alert> */}
          <Typography variant="body1" paragraph>
            Your report request has been submitted and is being processed.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to requests list...
          </Typography>
          <CircularProgress size={30} sx={{ mt: 3 }} />
        </Box>
      </Box>
    );
  }

  // REPLACE WITH — pass all collected data:
  if (isPreview) {
    return (
      <Step3Preview
        onBack={() => setIsPreview(false)}
        onSend={handleSendClick}        // ← same handler as the main Send button
        settingsData={settingsData}
        companiesData={companiesData}
        reportData={getValues()}   // ← current form values from step 3
        isSubmitting={isSubmitting}
      />
    );
  }

  return (
    <Box sx={{ pb: 12 }}>
      {/* Error Alert */}
      {submitError && (
        <Box sx={{ maxWidth: 850, mx: "auto", mt: 2, mb: 2 }}>
          <Alert severity="error" sx={{ borderRadius: "12px" }}>
            {submitError}
          </Alert>
        </Box>
      )}

      {/* Summary Alert */}
      {/* {settingsData && companiesData && (
        <Box sx={{ maxWidth: 850, mx: "auto", mt: 2, mb: 2 }}>
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            <Typography variant="subtitle2" gutterBottom>
              Request Summary:
            </Typography>
            <Typography variant="body2">
              <strong>Request:</strong> {settingsData.report_name}<br />
              <strong>Period:</strong> {settingsData.period} {settingsData.monthLabel || settingsData.quarterLabel || settingsData.annual || ''}<br />
              <strong>Companies:</strong> {companiesData.selectedCompanies?.length || 0} selected<br />
              <strong>Industries:</strong> {companiesData.industries?.join(', ') || 'None selected'}<br />
              <strong>Due Date:</strong> {settingsData.dueDate || 'Not set'}
            </Typography>
          </Alert>
        </Box>
      )} */}

      <Box sx={{ maxWidth: 850, mx: "auto", mt: 4 }}>
        {/* 1. KPI Section */}
        <SectionWrapper
          title="KPI"
          icon={<BarChartOutlinedIcon sx={{ fontSize: 20, color: "#8B5E3C" }} />}
        >
          {/* <Controller
            name="metrics"
            control={control}
            render={({ field }) => (
              <>
                {field.value?.map((metric, index) => (
                  <Box key={metric.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                    <DragIndicatorIcon sx={{ color: "#9CA3AF", fontSize: 18 }} />

                    <FormControl sx={{ minWidth: 220 }} error={!!errors.metrics?.[index]?.type}>
                      <Select
                        size="small"
                        value={metric.type}
                        onChange={(e) => {
                          const newMetrics = [...field.value];
                          newMetrics[index] = { ...metric, type: e.target.value };
                          field.onChange(newMetrics);
                        }}
                        displayEmpty
                        renderValue={(selected) => selected || "Select metric"}
                        sx={{ borderRadius: "20px", bgcolor: "white" }}
                      >
                        <MenuItem value="Revenue">Revenue</MenuItem>
                        <MenuItem value="Cash Balance">Cash Balance</MenuItem>
                        <MenuItem value="Runway">Runway</MenuItem>
                        <MenuItem value="Net Income">Net Income</MenuItem>
                        <MenuItem value="Total Headcount">Total Headcount</MenuItem>
                      </Select>
                      {errors.metrics?.[index]?.type && (
                        <Typography variant="caption" color="error">
                          {errors.metrics[index].type.message}
                        </Typography>
                      )}
                    </FormControl>

                    <TextField
                      fullWidth
                      size="small"
                      value={metric.description || ''}
                      onChange={(e) => {
                        const newMetrics = [...field.value];
                        newMetrics[index] = { ...metric, description: e.target.value };
                        field.onChange(newMetrics);
                      }}
                      placeholder="Description (Optional)"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" }
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMetric(metric.id)}
                      disabled={field.value.length <= 1}
                    >
                      <CloseIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                    </IconButton>
                  </Box>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={handleAddMetric}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    borderRadius: "20px",
                    borderColor: "#E5E7EB",
                    color: "#1A1919",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderStyle: "dashed",
                    "&:hover": { borderStyle: "dashed" }
                  }}
                >
                  Add Metric
                </Button>
                {errors.metrics && !errors.metrics.message && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    At least one metric is required
                  </Typography>
                )}
              </>
            )}
          /> */}
          <Controller
            name="metrics"
            control={control}
            render={({ field }) => (
              <>
                {KPI_METRICS.map((metricDef, index) => {
                  const current = field.value?.[index] || { label: metricDef.label, description: "" };
                  return (
                    <Box key={metricDef.label} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                      <DragIndicatorIcon sx={{ color: "#9CA3AF", fontSize: 18 }} />

                      {/* Fixed read-only label pill */}
                      <Box sx={{
                        minWidth: 220, px: 2, height: 40,
                        display: "flex", alignItems: "center",
                        borderRadius: "20px", border: "1px solid #E5E7EB",
                        bgcolor: "white", fontSize: "14px", color: "#1A1919", fontWeight: 500,
                      }}>
                        {metricDef.label}
                      </Box>

                      <TextField
                        fullWidth size="small"
                        value={current.description || ""}
                        onChange={(e) => {
                          const updated = [...(field.value || [])];
                          updated[index] = { ...current, description: e.target.value };
                          field.onChange(updated);
                        }}
                        placeholder="Description (Optional)"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" } }}
                      />
                    </Box>
                  );
                })}
              </>
            )}
          />
        </SectionWrapper>

        {/* 2. Documents Section */}
        <SectionWrapper
          title="Documents"
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 20, color: "#8B5E3C" }} />}
        >
          <Typography sx={{ fontSize: "14px", color: "#4B5563", mb: 1 }}>
            Add a description if you need specific documents from the partner
          </Typography>
          <Controller
            name="documentsDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter description..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" } }}
              />
            )}
          />
        </SectionWrapper>

        {/* 3. News Section */}
        <SectionWrapper
          title="News"
          icon={<BoltOutlinedIcon sx={{ fontSize: 20, color: "#8B5E3C" }} />}
        >
          <Typography sx={{ fontSize: "14px", color: "#4B5563", mb: 1 }}>
            Add a description if you need specific information from the partner
          </Typography>
          <Controller
            name="newsDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter description..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" } }}
              />
            )}
          />
        </SectionWrapper>

        {/* 4. Custom Fields Section */}
        <SectionWrapper
          title="Custom Fields"
          icon={<SettingsOutlinedIcon sx={{ fontSize: 20, color: "#8B5E3C" }} />}
          action={
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={handleAddCustomField}
              sx={{ borderRadius: "20px", textTransform: "none", color: "#1A1919", borderColor: "#E5E7EB" }}
            >
              Add
            </Button>
          }
        >
          <Controller
            name="customFields"
            control={control}
            render={({ field }) => (
              <>
                {field.value?.map((customField, index) => (
                  <Box key={customField.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                    <DragIndicatorIcon sx={{ color: "#9CA3AF", fontSize: 18 }} />

                    {/* <TextField
                      size="small"
                      value={customField.field_name || ''}
                      onChange={(e) => {
                        const newFields = [...field.value];
                        newFields[index] = { ...customField, field_name: e.target.value };
                        field.onChange(newFields);
                      }}
                      placeholder="Field name"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" },
                        flex: 1
                      }}
                      error={!!errors.customFields?.[index]?.field_name}
                      helperText={errors.customFields?.[index]?.field_name?.message}
                    /> */}

                    <FormControl sx={{ flex: 1 }} error={!!errors.customFields?.[index]?.field_name}>
                      <Select
                        size="small"
                        value={customField.field_name || ''}
                        onChange={(e) => {
                          const newFields = [...field.value];
                          newFields[index] = { ...customField, field_name: e.target.value };
                          field.onChange(newFields);
                        }}
                        displayEmpty
                        renderValue={(selected) => selected || "Select field"}
                        sx={{ borderRadius: "20px", bgcolor: "white" }}
                      >
                        {CUSTOM_FIELD_OPTIONS.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      value={customField.field_description || ''}
                      onChange={(e) => {
                        const newFields = [...field.value];
                        newFields[index] = { ...customField, field_description: e.target.value };
                        field.onChange(newFields);
                      }}
                      placeholder="Description (Optional)"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "20px", bgcolor: "white" },
                        flex: 1
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveCustomField(customField.id)}
                    >
                      <CloseIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                    </IconButton>
                  </Box>
                ))}
              </>
            )}
          />
        </SectionWrapper>
      </Box>

      {/* Modals */}
      <SendReportModal
        open={openSendModal}
        onClose={() => setOpenSendModal(false)}
        onSaveDraft={handleSaveDraft}
        onContinue={() => setOpenSendModal(false)}
        isSubmitting={isSubmitting}
      />

      <LeaveWithoutSavingModal
        open={openLeaveModal}
        onClose={() => setOpenLeaveModal(false)}
        onDiscard={handleDiscard}
        onSaveDraft={handleSaveDraft}
      />

      {/* Fixed Footer */}

      <StepActions
        onBack={() => navigate(
          location.state?.requestId
            ? `/create-request/${location.state.requestId}/companies`
            : `/create-request/companies`,
          {
            state: {
              settingsData: location.state?.settingsData,
              company: location.state?.company || [],
              companiesData: location.state?.company || [],
              requestId: location.state?.requestId || null,
              isEditMode: location.state?.isEditMode || false,
            },
          }
        )}
        onPreview={() => setIsPreview(true)}
        onSave={() => setOpenLeaveModal(true)}
        onNext={handleSendClick}
        showPreview={true}
        isValid={!isSubmitting}
        leftOffset={240}   // ← pass sidebar width as prop
        nextLabel={isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Send"}
      />

    </Box>
  );
};

export default Step3Report;